import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Archive, ChevronRight, Search } from 'lucide-react';
import { API_URL, ARCHIVE_SECTIONS, CATEGORIES, slugify } from '../constants';

const sectionColors = [
  { bg: 'bg-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', gradient: 'from-emerald-600 to-emerald-800' },
  { bg: 'bg-blue-700', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', gradient: 'from-blue-600 to-blue-800' },
  { bg: 'bg-purple-700', light: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', gradient: 'from-purple-600 to-purple-800' },
  { bg: 'bg-amber-700', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', gradient: 'from-amber-600 to-amber-800' },
  { bg: 'bg-rose-700', light: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', gradient: 'from-rose-600 to-rose-800' },
];

const ArchivePage = () => {
  const [search, setSearch] = useState('');
  const [, setApiCategories] = useState([]);
  const [, setCounts] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/categories/`).then(r => r.json()).then(d => setApiCategories(Array.isArray(d) ? d : (d.results || []))).catch(() => {});
    // Fetch subcategory counts
    fetch(`${API_URL}/subcategories/`).then(r => r.json()).then(d => {
      const arr = Array.isArray(d) ? d : (d.results || []);
      const c = {};
      arr.forEach(sub => {
        const cat = sub.category_name || sub.category;
        c[cat] = (c[cat] || 0) + 1;
      });
      setCounts(c);
    }).catch(() => {});
  }, []);

  const filtered = ARCHIVE_SECTIONS.filter(s =>
    s.category.toLowerCase().includes(search.toLowerCase()) ||
    s.title.toLowerCase().includes(search.toLowerCase()) ||
    CATEGORIES[s.category]?.some(sub => sub.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Archive className="w-8 h-8 text-emerald-700" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">The Archive</h1>
          <p className="text-gray-600">Explore all categories of Gbongan's documented legacy</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-lg mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search categories or subcategories..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      {/* Category Cards */}
      <div className="space-y-8">
        {filtered.map((section, i) => {
          const color = sectionColors[i % sectionColors.length];
          const subs = CATEGORIES[section.category] || [];
          return (
            <div key={section.id} className={`${color.light} ${color.border} border-2 rounded-2xl overflow-hidden hover:shadow-lg transition`}>
              {/* Category Header */}
              <Link to={`/archive/${slugify(section.category)}`} className={`bg-gradient-to-r ${color.gradient} text-white p-6 flex items-center justify-between hover:opacity-95 transition`}>
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{section.icon}</span>
                  <div>
                    <h2 className="text-xl font-bold">{section.category}</h2>
                    <p className="text-white/80 text-sm mt-1">{section.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <span className="text-sm font-medium hidden sm:block">View All</span>
                  <ChevronRight className="w-5 h-5" />
                </div>
              </Link>

              {/* Subcategories Grid */}
              <div className="p-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Subcategories ({subs.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {subs.map(sub => (
                    <Link
                      key={sub}
                      to={`/archive/${slugify(section.category)}/${slugify(sub)}`}
                      className="bg-white border border-gray-200 rounded-xl px-4 py-3 hover:shadow-md hover:border-emerald-300 transition flex items-center justify-between group"
                    >
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium leading-tight">{sub}</span>
                      <ChevronRight className={`w-4 h-4 ${color.text} opacity-0 group-hover:opacity-100 transition flex-shrink-0 ml-2`} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Archive className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No categories found for "{search}"</p>
          <button onClick={() => setSearch('')} className="mt-4 text-emerald-600 hover:underline">Clear search</button>
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
